import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, UserPlus, UserMinus, ChevronDown, CreditCard, Search, Users } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_MAP = {
  valid: { label: 'Valide', color: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
  verification: { label: 'En vérification', color: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30' },
  expired: { label: 'Expiré', color: 'bg-red-500/15 text-red-400 border-red-500/30' },
};

export default function AdminIBANPool() {
  const { token } = useAuth();
  const h = { Authorization: `Bearer ${token}` };

  const [ibans, setIbans] = useState([]);
  const [analysts, setAnalysts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [showAssignAnalyst, setShowAssignAnalyst] = useState(null);
  const [showAssignClient, setShowAssignClient] = useState(null);
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [form, setForm] = useState({ iban: '', bic: '', bank_name: '', account_holder: '', country: '', logo_base64: '' });

  const fetchData = useCallback(async () => {
    try {
      const [ibRes, anRes] = await Promise.all([
        axios.get(`${API}/iban-pool`, { headers: h }),
        axios.get(`${API}/analysts`, { headers: h }).catch(() => ({ data: [] })),
      ]);
      setIbans(ibRes.data || []);
      const anList = Array.isArray(anRes.data) ? anRes.data : (anRes.data?.analysts || []);
      setAnalysts(anList);
    } catch (e) {
      toast.error('Erreur chargement');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    if (!form.iban || !form.account_holder) { toast.error('IBAN et titulaire requis'); return; }
    try {
      await axios.post(`${API}/iban-pool`, form, { headers: { ...h, 'Content-Type': 'application/json' } });
      toast.success('RIB créé');
      setShowCreate(false);
      setForm({ iban: '', bic: '', bank_name: '', account_holder: '', country: '', logo_base64: '' });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleUpdate = async () => {
    if (!showEdit) return;
    try {
      await axios.put(`${API}/iban-pool/${showEdit.id}`, {
        iban: showEdit.iban, bic: showEdit.bic, bank_name: showEdit.bank_name,
        account_holder: showEdit.account_holder, country: showEdit.country, status: showEdit.status,
        logo_base64: showEdit.logo_base64,
      }, { headers: { ...h, 'Content-Type': 'application/json' } });
      toast.success('RIB mis à jour');
      setShowEdit(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = async (id) => {
    if (!confirm('Supprimer ce RIB ? Il sera retiré de tous les clients.')) return;
    try {
      await axios.delete(`${API}/iban-pool/${id}`, { headers: h });
      toast.success('RIB supprimé');
      fetchData();
    } catch (e) { toast.error('Erreur'); }
  };

  const handleAssignAnalyst = async (ibanId, analystId) => {
    try {
      await axios.post(`${API}/iban-pool/${ibanId}/assign-analyst`, { analyst_ids: [analystId] }, { headers: { ...h, 'Content-Type': 'application/json' } });
      toast.success('Analyste attribué');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleUnassignAnalyst = async (ibanId, analystId) => {
    try {
      await axios.post(`${API}/iban-pool/${ibanId}/unassign-analyst`, { analyst_id: analystId }, { headers: { ...h, 'Content-Type': 'application/json' } });
      toast.success('Analyste retiré');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleAssignClient = async (ibanId, clientId) => {
    try {
      await axios.post(`${API}/iban-pool/${ibanId}/assign-client`, { client_id: clientId }, { headers: { ...h, 'Content-Type': 'application/json' } });
      toast.success('RIB attribué au client');
      setShowAssignClient(null);
      setClientSearch('');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleUnassignClient = async (ibanId, clientId) => {
    try {
      await axios.post(`${API}/iban-pool/${ibanId}/unassign-client`, { client_id: clientId }, { headers: { ...h, 'Content-Type': 'application/json' } });
      toast.success('RIB retiré du client');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const openAssignClient = async (ibanId) => {
    setShowAssignClient(ibanId);
    setClientSearch('');
    try {
      const res = await axios.get(`${API}/clients?limit=500`, { headers: h });
      setClients(Array.isArray(res.data) ? res.data : (res.data?.clients || []));
    } catch { setClients([]); }
  };

  const filtered = ibans.filter(i => {
    if (statusFilter !== 'all' && i.status !== statusFilter) return false;
    if (filter) {
      const q = filter.toLowerCase();
      return i.iban?.toLowerCase().includes(q) || i.account_holder?.toLowerCase().includes(q) || i.bank_name?.toLowerCase().includes(q);
    }
    return true;
  });

  const availableAnalysts = (ibanId) => {
    const iban = ibans.find(i => i.id === ibanId);
    const assigned = iban?.assigned_analysts || [];
    return analysts.filter(a => !assigned.includes(a.user_id || a.id));
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-iban-pool">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Gestion des RIB</h1>
            <p className="text-sm text-muted-foreground">Pool centralisé — créez, attribuez et gérez les RIB</p>
          </div>
          <Button onClick={() => setShowCreate(true)} data-testid="create-iban-btn">
            <Plus className="w-4 h-4 mr-2" /> Nouveau RIB
          </Button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Rechercher IBAN, titulaire, banque..." value={filter} onChange={e => setFilter(e.target.value)} className="pl-9" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]"><SelectValue placeholder="Statut" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              <SelectItem value="valid">Valide</SelectItem>
              <SelectItem value="verification">En vérification</SelectItem>
              <SelectItem value="expired">Expiré</SelectItem>
            </SelectContent>
          </Select>
          <Badge variant="outline">{filtered.length} RIB</Badge>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Chargement...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <CreditCard className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">Aucun RIB dans le pool</p>
            <Button variant="outline" className="mt-3" onClick={() => setShowCreate(true)}>Créer un RIB</Button>
          </div>
        ) : (
          <div className="border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/30">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">IBAN</th>
                  <th className="text-left px-4 py-3 font-medium">Titulaire</th>
                  <th className="text-left px-4 py-3 font-medium">Banque</th>
                  <th className="text-left px-4 py-3 font-medium">Statut</th>
                  <th className="text-left px-4 py-3 font-medium">Analystes</th>
                  <th className="text-left px-4 py-3 font-medium">Clients</th>
                  <th className="text-right px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(iban => {
                  const st = STATUS_MAP[iban.status] || STATUS_MAP.valid;
                  return (
                    <tr key={iban.id} className="border-t hover:bg-muted/10 transition-colors">
                      <td className="px-4 py-3 font-mono text-xs">{iban.iban}</td>
                      <td className="px-4 py-3">{iban.account_holder}</td>
                      <td className="px-4 py-3 text-muted-foreground">{iban.bank_name || '-'}</td>
                      <td className="px-4 py-3"><Badge variant="outline" className={st.color}>{st.label}</Badge></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 flex-wrap">
                          {(iban.analyst_names || []).map(a => (
                            <Badge key={a.id} variant="secondary" className="text-xs gap-1">
                              {a.name}
                              <button onClick={() => handleUnassignAnalyst(iban.id, a.id)} className="ml-0.5 hover:text-destructive"><UserMinus className="w-3 h-3" /></button>
                            </Badge>
                          ))}
                          <button onClick={() => setShowAssignAnalyst(iban.id)} className="p-1 rounded hover:bg-muted"><UserPlus className="w-3.5 h-3.5 text-muted-foreground" /></button>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 flex-wrap">
                          {(iban.client_names || []).map(c => (
                            <Badge key={c.id} variant="outline" className="text-xs gap-1">
                              {c.name}
                              <button onClick={() => handleUnassignClient(iban.id, c.id)} className="ml-0.5 hover:text-destructive"><UserMinus className="w-3 h-3" /></button>
                            </Badge>
                          ))}
                          {iban.status === 'valid' && (
                            <button onClick={() => openAssignClient(iban.id)} className="p-1 rounded hover:bg-muted" title="Attribuer à un client" data-testid={`assign-client-${iban.id}`}><UserPlus className="w-3.5 h-3.5 text-blue-400" /></button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setShowEdit({ ...iban })} data-testid={`edit-iban-${iban.id}`}>
                            <Edit2 className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(iban.id)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Nouveau RIB</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>IBAN *</Label><Input value={form.iban} onChange={e => setForm(p => ({ ...p, iban: e.target.value }))} placeholder="FR76..." className="mt-1 font-mono" /></div>
            <div><Label>BIC</Label><Input value={form.bic} onChange={e => setForm(p => ({ ...p, bic: e.target.value }))} placeholder="BNPAFRPP" className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Banque</Label><Input value={form.bank_name} onChange={e => setForm(p => ({ ...p, bank_name: e.target.value }))} placeholder="BNP Paribas" className="mt-1" /></div>
              <div><Label>Pays</Label><Input value={form.country} onChange={e => setForm(p => ({ ...p, country: e.target.value }))} placeholder="FR" className="mt-1" /></div>
            </div>
            <div><Label>Titulaire du compte *</Label><Input value={form.account_holder} onChange={e => setForm(p => ({ ...p, account_holder: e.target.value }))} placeholder="SwissBorg SAS" className="mt-1" /></div>
            <div>
              <Label>Logo (optionnel)</Label>
              <Input type="file" accept="image/*" className="mt-1" data-testid="create-iban-logo-input" onChange={e => {
                const file = e.target.files?.[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onload = (ev) => setForm(p => ({ ...p, logo_base64: ev.target.result }));
                  reader.readAsDataURL(file);
                }
              }} />
              {form.logo_base64 && <img src={form.logo_base64} alt="Logo" className="h-8 mt-2 object-contain" />}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Annuler</Button>
            <Button onClick={handleCreate}>Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!showEdit} onOpenChange={() => setShowEdit(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Modifier le RIB</DialogTitle></DialogHeader>
          {showEdit && (
            <div className="space-y-4 py-2">
              <div><Label>IBAN</Label><Input value={showEdit.iban} onChange={e => setShowEdit(p => ({ ...p, iban: e.target.value }))} className="mt-1 font-mono" /></div>
              <div><Label>BIC</Label><Input value={showEdit.bic || ''} onChange={e => setShowEdit(p => ({ ...p, bic: e.target.value }))} className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Banque</Label><Input value={showEdit.bank_name || ''} onChange={e => setShowEdit(p => ({ ...p, bank_name: e.target.value }))} className="mt-1" /></div>
                <div><Label>Pays</Label><Input value={showEdit.country || ''} onChange={e => setShowEdit(p => ({ ...p, country: e.target.value }))} className="mt-1" /></div>
              </div>
              <div><Label>Titulaire</Label><Input value={showEdit.account_holder} onChange={e => setShowEdit(p => ({ ...p, account_holder: e.target.value }))} className="mt-1" /></div>
              <div>
                <Label>Logo</Label>
                <Input type="file" accept="image/*" className="mt-1" data-testid="edit-iban-logo-input" onChange={e => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = (ev) => setShowEdit(p => ({ ...p, logo_base64: ev.target.result }));
                    reader.readAsDataURL(file);
                  }
                }} />
                {showEdit.logo_base64 && <img src={showEdit.logo_base64} alt="Logo" className="h-8 mt-2 object-contain" />}
              </div>
              <div>
                <Label>Statut</Label>
                <Select value={showEdit.status} onValueChange={v => setShowEdit(p => ({ ...p, status: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="valid">Valide</SelectItem>
                    <SelectItem value="verification">En vérification</SelectItem>
                    <SelectItem value="expired">Expiré</SelectItem>
                  </SelectContent>
                </Select>
                {showEdit.status === 'expired' && (
                  <p className="text-xs text-destructive mt-1">Attention : passer en expiré retirera ce RIB de tous les clients</p>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEdit(null)}>Annuler</Button>
            <Button onClick={handleUpdate}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Analyst Dialog */}
      <Dialog open={!!showAssignAnalyst} onOpenChange={() => setShowAssignAnalyst(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Attribuer un analyste</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2 max-h-[300px] overflow-y-auto">
            {showAssignAnalyst && availableAnalysts(showAssignAnalyst).length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">Tous les analystes sont déjà attribués</p>
            ) : (
              showAssignAnalyst && availableAnalysts(showAssignAnalyst).map(a => (
                <button key={a.user_id || a.id} onClick={() => { handleAssignAnalyst(showAssignAnalyst, a.user_id || a.id); setShowAssignAnalyst(null); }}
                  className="flex items-center gap-3 w-full p-3 rounded-lg border hover:bg-muted/30 transition-colors">
                  <Users className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm">{a.first_name || a.user?.first_name} {a.last_name || a.user?.last_name}</span>
                </button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Client Dialog */}
      <Dialog open={!!showAssignClient} onOpenChange={() => setShowAssignClient(null)}>
        <DialogContent className="sm:max-w-md" data-testid="assign-client-dialog">
          <DialogHeader><DialogTitle>Attribuer à un client</DialogTitle></DialogHeader>
          <Input placeholder="Rechercher un client..." value={clientSearch} onChange={e => setClientSearch(e.target.value)} className="mb-2" data-testid="assign-client-search" />
          <div className="space-y-1 max-h-[300px] overflow-y-auto">
            {clients.filter(c => {
              if (!clientSearch) return true;
              const q = clientSearch.toLowerCase();
              const fn = c.first_name || c.user?.first_name || '';
              const ln = c.last_name || c.user?.last_name || '';
              const em = c.email || c.user?.email || '';
              return `${fn} ${ln} ${em}`.toLowerCase().includes(q);
            }).slice(0, 30).map(c => {
              const cId = c.user_id || c.id;
              const iban = ibans.find(i => i.id === showAssignClient);
              const alreadyAssigned = (iban?.assigned_clients || []).includes(cId);
              return (
                <button key={cId} disabled={alreadyAssigned}
                  onClick={() => handleAssignClient(showAssignClient, cId)}
                  className={`flex items-center justify-between w-full p-2.5 rounded-lg border text-sm transition-colors ${alreadyAssigned ? 'opacity-40' : 'hover:bg-muted/30'}`}>
                  <span>{c.first_name || c.user?.first_name} {c.last_name || c.user?.last_name}</span>
                  {alreadyAssigned && <Badge variant="outline" className="text-[10px]">Déjà attribué</Badge>}
                </button>
              );
            })}
            {clients.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">Aucun client trouvé</p>}
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
